import { useId, useState } from "react";
import type { FormEvent } from "react";
import { problemFieldErrors, problemMessage } from "@/api/client";
import type {
  CreateShiftRequest,
  Guid,
  PayrollEmployee,
  Shift,
  ShiftStatus,
  UpdateShiftRequest,
} from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./ShiftDialog.css";

/**
 * ef-040-3 — the "New shift" / "Edit shift" dialog Schedule.tsx opens from its own header
 * action or a row's Edit button. A dialog component, deliberately separate from
 * Schedule.tsx's own CSS budget — same reasoning `W4Dialog.tsx` gives for splitting out of
 * H2a.tsx: `deploy/lib/css-budget.sh` gates `pages/*.css` at 250 lines, and component CSS
 * living beside a component under `components/` is the intended escape valve (ADR 0009 §5).
 *
 * EMPLOYEE IS PICKED ONCE, ON CREATE, AND NEVER SHOWN AS EDITABLE AFTER — `UpdateShiftRequest`
 * (api/types.ts, mirroring `ScheduleContracts.cs`) has no `employeeId` field at all, so an
 * editable picker in edit mode would be a control that always does nothing when used. Edit
 * mode instead states the name as plain text.
 *
 * STATUS ONLY APPEARS WHEN EDITING. A new shift is always created `draft` server-side
 * (`ScheduleEndpoints.cs`'s own default) — offering a status picker on create would be
 * asking for a value the request body has nowhere to put.
 */
interface ShiftFormState {
  employeeId: string;
  scheduledStart: string;
  scheduledEnd: string;
  place: string;
  note: string;
  status: ShiftStatus;
}

function blankForm(defaultEmployeeId: string): ShiftFormState {
  return {
    employeeId: defaultEmployeeId,
    scheduledStart: "",
    scheduledEnd: "",
    place: "",
    note: "",
    status: "draft",
  };
}

/** `datetime-local`-shaped value, browser-local — the same shape `schedule.spec.ts`'s own
 *  `toLocalInput()` fills these fields with. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formFromShift(shift: Shift): ShiftFormState {
  return {
    employeeId: shift.employeeId,
    scheduledStart: toLocalInput(shift.scheduledStart),
    scheduledEnd: toLocalInput(shift.scheduledEnd),
    place: shift.place ?? "",
    note: shift.note ?? "",
    status: shift.status,
  };
}

const STATUS_OPTIONS: { value: ShiftStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "cancelled", label: "Cancelled" },
];

export function ShiftDialog({
  employees,
  shift,
  onClose,
  onCreate,
  onUpdate,
}: {
  employees: PayrollEmployee[];
  /** `null` opens the dialog in create mode; a `Shift` opens it pre-seeded for editing. */
  shift: Shift | null;
  /** Called on Cancel, Escape, an overlay click, AND after a successful save — same
   *  division `W4Dialog.tsx` uses between "close the dialog" and "where focus goes
   *  next", which stays the caller's job (it holds the trigger ref). */
  onClose: () => void;
  onCreate: (body: CreateShiftRequest) => Promise<Shift>;
  onUpdate: (id: Guid, body: UpdateShiftRequest) => Promise<Shift>;
}) {
  const [form, setForm] = useState<ShiftFormState>(() =>
    shift ? formFromShift(shift) : blankForm(employees[0]?.id ?? ""),
  );
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const employeeId = useId();
  const startId = useId();
  const endId = useId();
  const placeId = useId();
  const noteId = useId();
  const statusId = useId();

  const formId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);

    /*
     * @ui-check: client-side ONLY — the server's own checks (ScheduleEndpoints.cs's
     * required fields, and the `shift_ends_after_start` CHECK) still run after this and
     * remain the actual contract enforcement. This guard exists so a farmer sees the
     * same reason immediately, without a round trip, for the one case worth catching
     * here: a zero-length or backwards shift.
     */
    const missing: Record<string, string[]> = {};
    if (!shift && form.employeeId === "") missing.employeeId = ["Choose an employee."];
    if (form.scheduledStart === "") missing.scheduledStart = ["Required."];
    if (form.scheduledEnd === "") {
      missing.scheduledEnd = ["Required."];
    } else if (form.scheduledStart !== "" && form.scheduledEnd <= form.scheduledStart) {
      missing.scheduledEnd = ["End must be after start."];
    }
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      setFormError(Object.values(missing).flat()[0] ?? null);
      return;
    }

    setSaving(true);
    try {
      const place = form.place.trim() === "" ? null : form.place.trim();
      const note = form.note.trim() === "" ? null : form.note.trim();
      const scheduledStart = new Date(form.scheduledStart).toISOString();
      const scheduledEnd = new Date(form.scheduledEnd).toISOString();

      if (shift) {
        await onUpdate(shift.id, {
          status: form.status,
          scheduledStart,
          scheduledEnd,
          place,
          note,
        });
      } else {
        await onCreate({ employeeId: form.employeeId, scheduledStart, scheduledEnd, place, note });
      }
      onClose();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not save this shift."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title={<>{shift ? `Edit shift — ${shift.employeeName}` : "New shift"}</>}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {/* Exact labels, not "Save changes" — schedule.spec.ts's own save-button
                locator is `/^(save|update)( shift)?$/i` for an edit and
                `/^(save|create|add)( shift)?$/i` for a create, both anchored at both
                ends, so "Save" and "Save shift" are the widest labels that satisfy
                either regex exactly. */}
            {saving ? "Saving…" : shift ? "Save" : "Save shift"}
          </button>
        </>
      }
    >
      <form id={formId} className="shift-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        {shift ? (
          <p className="shift-dialog__employee">{shift.employeeName}</p>
        ) : (
          <div className="shift-dialog__field">
            <label htmlFor={employeeId}>Employee</label>
            <select
              id={employeeId}
              value={form.employeeId}
              onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
              aria-invalid={fieldErrors?.employeeId ? true : undefined}
            >
              <option value="">Choose an employee</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
            {fieldErrors?.employeeId && (
              <p className="shift-dialog__field-error" role="alert">
                {fieldErrors.employeeId[0]}
              </p>
            )}
          </div>
        )}

        {shift && (
          <div className="shift-dialog__field">
            <label htmlFor={statusId}>Status</label>
            <select
              id={statusId}
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as ShiftStatus })}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="shift-dialog__field">
          <label htmlFor={startId}>Start</label>
          <input
            id={startId}
            type="datetime-local"
            value={form.scheduledStart}
            onChange={(e) => setForm({ ...form, scheduledStart: e.target.value })}
            aria-invalid={fieldErrors?.scheduledStart ? true : undefined}
          />
        </div>

        <div className="shift-dialog__field">
          <label htmlFor={endId}>End</label>
          <input
            id={endId}
            type="datetime-local"
            value={form.scheduledEnd}
            onChange={(e) => setForm({ ...form, scheduledEnd: e.target.value })}
            aria-invalid={fieldErrors?.scheduledEnd ? true : undefined}
          />
          {fieldErrors?.scheduledEnd && (
            <p className="shift-dialog__field-error" role="alert">
              {fieldErrors.scheduledEnd[0]}
            </p>
          )}
        </div>

        <div className="shift-dialog__field">
          <label htmlFor={placeId}>Place (optional)</label>
          <input
            id={placeId}
            type="text"
            maxLength={200}
            value={form.place}
            onChange={(e) => setForm({ ...form, place: e.target.value })}
          />
        </div>

        <div className="shift-dialog__field">
          <label htmlFor={noteId}>Note (optional)</label>
          <input
            id={noteId}
            type="text"
            maxLength={500}
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
        </div>

        {formError && !fieldErrors?.scheduledEnd && !fieldErrors?.employeeId && (
          <p className="shift-dialog__form-error" role="alert">
            {formError}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
